import { WorkingHours, WorkInterval } from '@chilipiper/api-client-user-availability'
import React, { useMemo, useState } from 'react'
import { Flex, styled, Text } from '@chilipiper/design-system'
import { UnstyledButton } from '@chilipiper/design-system/src/new'
import { last } from 'lodash'
import {
  add,
  differenceInMilliseconds,
  endOfDay,
  format,
  isSameDay,
  parse,
} from '@chilipiper/date-time'
import { convertMsToMinutes } from '@chilipiper/utils'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import { WorkingHoursSlot } from './WorkingHoursSlot'
import { createDefaultWorkInterval } from '@/components/availability-schedules/utils'
import { findOverlaps } from '@/components/availability-schedules/working-hours/utils'
import { CustomSwitch } from '@/components/availability-schedules/working-hours/CustomSwitch'

const capitalizeFirstLetter = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const MINUTES_TO_ADD = 30

const StyledButton = styled(UnstyledButton)`
  pointer-events: auto;
  :hover {
    background-color: ${({ theme }) => theme.colors['bg/list-item-hovered']};
  }
  :focus-visible {
    box-shadow: ${({ theme }) => theme.shadows['border/focus-indicator']};
  }
  :focus {
    box-shadow: ${({ theme }) => theme.shadows['border/focus-indicator']};
  }
`

const getBorderRadius = (isFirst: boolean, isLast: boolean) => {
  return {
    borderTopRightRadius: isFirst ? 8 : 0,
    borderTopLeftRadius: isFirst ? 8 : 0,
    borderBottomRightRadius: isLast ? 8 : 0,
    borderBottomLeftRadius: isLast ? 8 : 0,
  }
}

export const WorkingHoursDayRow = ({
  day,
  workIntervals,
  onChange,
  workingHours,
  isLast,
  isFirst,
}: {
  day: string
  isFirst: boolean
  isLast: boolean
  onChange: (workingHours: WorkingHours) => void
  workIntervals: WorkInterval[]
  workingHours: WorkingHours
}) => {
  const isActive = useMemo(() => {
    return workIntervals.length > 0
  }, [workIntervals.length])

  const dayOverlaps = useMemo(() => {
    const overlaps = findOverlaps(workingHours)
    return overlaps[day] ?? []
  }, [day, workingHours])

  const [previousValue, setPreviousValue] = useState<WorkingHours>({})

  return (
    <StyledButton
      px={4}
      {...getBorderRadius(isFirst, isLast)}
      onClick={() => {
        const isActiveToggle = !isActive

        if (isActiveToggle) {
          const defaultValue = {
            [day]: [createDefaultWorkInterval()],
          }

          if (!isEmpty(previousValue)) {
            onChange({ ...workingHours, ...previousValue })
          } else {
            onChange({ ...workingHours, ...defaultValue })
          }
        } else {
          const emptyWorkingHoursIntervals = {
            [day]: [],
          }

          onChange({ ...workingHours, ...emptyWorkingHoursIntervals })
          setPreviousValue({ [day]: workingHours[day] })
        }
      }}
    >
      <Flex alignItems='center' px={4} py={2}>
        <Flex gap={4}>
          <CustomSwitch isChecked={isActive} isDisabled={false} />
          <Text
            textStyle={isActive ? 'item-selected' : 'item-unselected'}
            color={'fg/major'}
            minWidth={'120px'}
          >
            {capitalizeFirstLetter(day)}
          </Text>
        </Flex>

        {workIntervals.length > 0 ? (
          <Flex flexDirection='column' py={1} gap={2}>
            {workIntervals.map((workInterval, index) => (
              <WorkingHoursSlot
                isOverlapping={dayOverlaps.some(overlappedInterval =>
                  isEqual(workInterval, overlappedInterval)
                )}
                onCopyIntervals={() => {
                  const newObject = Object.keys(workingHours).reduce((acc, key) => {
                    const existingIntervalsForDay = workingHours[key]

                    if (existingIntervalsForDay.length === 0) {
                      acc[key] = []
                    } else {
                      acc[key] = structuredClone(workIntervals)
                    }

                    return acc
                  }, {})

                  onChange(newObject)
                }}
                onAddSlot={() => {
                  const lastInterval = last(workIntervals)

                  const lastIntervalStartsAt = lastInterval?.startsAt
                  const lastIntervalDuration = lastInterval?.duration

                  if (lastIntervalStartsAt && lastIntervalDuration) {
                    const startsAtDate = parse(lastIntervalStartsAt, 'HH:mm:ss', new Date())

                    const lastIntervalEndsAtDate = add(startsAtDate, {
                      minutes: convertMsToMinutes(parseInt(lastIntervalDuration)),
                    })

                    const addedIntervalStartsAt = add(lastIntervalEndsAtDate, {
                      minutes: MINUTES_TO_ADD,
                    })
                    const oneHourInMs = 3600000

                    const addedIntervalEndsAt = add(addedIntervalStartsAt, {
                      minutes: convertMsToMinutes(oneHourInMs),
                    })

                    const durationToAdd = isSameDay(addedIntervalStartsAt, addedIntervalEndsAt)
                      ? oneHourInMs
                      : differenceInMilliseconds(
                          endOfDay(addedIntervalStartsAt),
                          addedIntervalStartsAt
                        )

                    const updatedDay = {
                      [day]: [
                        ...workIntervals,
                        {
                          startsAt: format(addedIntervalStartsAt, 'HH:mm:ss'),
                          duration: `${durationToAdd} milliseconds`,
                        },
                      ],
                    }

                    onChange({ ...workingHours, ...updatedDay })
                  }
                }}
                onRemoveSlot={() => {
                  const indexToRemove = index
                  const updatedDay = {
                    [day]: workIntervals.filter((_, idx) => idx !== indexToRemove),
                  }
                  onChange({ ...workingHours, ...updatedDay })
                }}
                onChangeStart={(startTime, updatedDuration) => {
                  const indexToUpdate = index
                  const updatedDay = {
                    [day]: workIntervals.map((interval, idx) => {
                      if (idx === indexToUpdate) {
                        interval.startsAt = `${startTime}:00`
                        interval.duration = updatedDuration
                      }
                      return interval
                    }),
                  }
                  onChange({ ...workingHours, ...updatedDay })
                }}
                isFirst={index === 0}
                key={`${day}-interval-${index}`}
                workInterval={workInterval}
              />
            ))}
          </Flex>
        ) : (
          <Flex
            justifyContent='start'
            flexGrow={1}
            pb={4}
            pt={'17px'}
            alignItems='center'
            h={'100%'}
            mt='1px'
          >
            <Text textStyle='item-unselected' color='fg/major'>
              Not Available
            </Text>
          </Flex>
        )}
      </Flex>
    </StyledButton>
  )
}
